// F3 (#809) TIER 3 — headless on-login re-inject of the offline-logout stash.
//
// Sibling of AutoRescueHeldCaptures. That component ships the device's LIVE held
// work (the `orphaned_capture_items` quarantine) to the recovery inbox. THIS one
// re-injects the OFFLINE-LOGOUT STASH: when a user signs out while offline, their
// un-uploaded counts can't be POSTed, so they are stashed in durable on-device
// IndexedDB that survives `disconnectAndClear` (stash-held-work-audit.ts →
// logout-cleanup.ts → held-work-offline-stash.ts), keyed to that user. Once the
// SAME user is authenticated and online again, this component restores that stash,
// routing each dimension to where an offline count belongs:
//   - the ps_crud-QUEUED counts are re-INSERTed into the LIVE capture path so they
//     re-enter the upload queue and SYNC (the queue drains on the device and the
//     counts become live capture_items) — what the surveyor expects on re-login;
//   - the already-failed `orphaned_capture_items` counts ship to the isolated
//     recovery inbox for admin Phase-2 placement (they can't sync as-is).
// The stash is cleared per chunk only after its handling is confirmed.
//
// When it runs (mirrors AutoRescueHeldCaptures so the two share one mental model):
//   - once the session is authenticated AND PowerSync's first sync has completed
//     (`hasSynced`) — catches a stash left by a PRIOR offline sign-out on this device
//     (IndexedDB persists across restarts). The first-sync gate guarantees a stashed
//     EDIT's synced-down row is present locally before the collision → PATCH re-apply
//     (see the `hasSynced` rationale on the gate below);
//   - again when the device comes back online — catches the case where the user
//     logged back in while still offline and only later regained the network.
//     Debounced (MIN_REINJECT_INTERVAL_MS) so a flapping radio can't exhaust the
//     inbox's per-user rate limit.
//
// SHARED-DEVICE SAFETY — the load-bearing invariant. The re-inject is scoped to
// the CURRENTLY-AUTHENTICATED user's id: it reads ONLY that user's stash slot
// (held-work-offline-stash.ts :: readStashedHeldWork compares the slot's userId),
// so user B logging in on a shared device never ships user A's stashed counts
// (the server attributes a recovery POST to the authed caller, so that would
// MISATTRIBUTE A's counts to B). A's stash waits, untouched, for A to return.
//
// Safety / idempotency: the re-queue reuses the original capture_items id, so the
// server resolves a same-id re-create idempotently (a retry never duplicates) and a
// still-un-syncable count is re-quarantined by the connector rather than wedging the
// queue; the inbox dimension upserts on (user, original_item_id). The stash is
// cleared per chunk ONLY after its handling is confirmed, so a failed/again-offline
// re-inject retries on the next trigger. Best-effort — a failure is reported to
// Sentry and left for the next trigger, never surfaced as UI.

import { useEffect, type ReactNode } from 'react'

import * as Sentry from '@sentry/react'

import { reinjectStashedHeldWork } from '@/hooks/powersync/logout-cleanup'
import type { RescueResult } from '@/hooks/powersync/rescue-held-captures'
import { useHasSynced } from '@/hooks/powersync/use-has-synced'

import { useAuth } from './auth-provider'

type Reinjector = (userId: string) => Promise<RescueResult>

// Module-level so the in-flight guard and debounce survive React re-mounts
// (including StrictMode's dev double-mount), preventing a duplicate ship per load.
let reinjectInFlight = false
// Sentinel "never successfully re-injected" so the first reconnect always clears
// the debounce; replaced with a real timestamp only after a successful ship.
let lastReinjectAt = Number.NEGATIVE_INFINITY
// A reconnecting/flapping radio can fire `online` repeatedly; ignore re-runs
// inside this window so we never burn the POST /capture/recovery-dump token
// bucket (10 / 60s per user). Mirrors AutoRescueHeldCaptures.
const MIN_REINJECT_INTERVAL_MS = 30_000

/** Test seam — reset the module-level guards between cases. */
export function __resetAutoReinjectForTest(): void {
  reinjectInFlight = false
  lastReinjectAt = Number.NEGATIVE_INFINITY
}

// Run one re-inject, guarding against an overlapping run. Concurrency only (one
// ship at a time), NOT lifetime — a later trigger after this resolves is allowed.
async function runReinject(
  userId: string,
  reinject: Reinjector,
): Promise<void> {
  if (reinjectInFlight) return
  reinjectInFlight = true
  try {
    await reinject(userId)
    // Stamp the debounce window ONLY after a SUCCESSFUL ship. Stamping before the
    // await would let a failed attempt (booted with a cached session while still
    // offline) arm the 30s window and suppress the first reconnect retry. The
    // in-flight guard already prevents overlap, so a failure falls through and the
    // next trigger retries. (mirrors AutoRescueHeldCaptures :: shipHeldCounts)
    lastReinjectAt = Date.now()
  } catch (err: unknown) {
    // Best-effort: the stash is NOT cleared on a throw (held-work-offline-stash.ts
    // :: reinjectStash), so the next trigger retries. Report it so a device
    // silently failing to re-inject stays visible to us.
    Sentry.captureException(err, {
      level: 'warning',
      extra: { phase: 'auto-reinject-stashed-work' },
    })
  } finally {
    reinjectInFlight = false
  }
}

export interface AutoReinjectStashedWorkProps {
  /** Test seam — inject a fake re-injector. Defaults to the real, IndexedDB- +
   *  network-wired re-inject (reinjectStashedHeldWork). */
  reinject?: Reinjector
}

export function AutoReinjectStashedWork({
  reinject = reinjectStashedHeldWork,
}: AutoReinjectStashedWorkProps = {}): ReactNode {
  // Auth gate FIRST — and deliberately WITHOUT calling any PowerSync hook here.
  // AuthGatedPowerSyncProvider renders its children WITHOUT a PowerSyncContext.Provider
  // until status === 'authenticated' (provider.tsx), so calling useHasSynced() (→
  // @powersync/react useStatus) before auth resolves would throw and CRASH the
  // boot/login render instead of merely deferring the re-inject (Codex P1). `!userId`
  // (not `=== undefined`) matches the write seam's guard (stashHeldWork's
  // `if (!userId) return`), so an empty-string principal never drives a re-inject —
  // symmetric refusal at both ends (audit-2 no-leak). Only mount the inner component —
  // which reads the first-sync signal — once authenticated, i.e. once the PowerSync
  // provider is actually present.
  const { status, user } = useAuth()
  const userId = user?.userID
  if (status !== 'authenticated' || !userId) return null
  return <AuthedReinjectStashedWork userId={userId} reinject={reinject} />
}

/**
 * The authenticated half. Mounted ONLY when status === 'authenticated', which is
 * exactly when AuthGatedPowerSyncProvider has wrapped the tree in a real
 * PowerSyncContext.Provider — so useHasSynced() (→ useStatus) is safe here.
 *
 * Gate the re-inject on PowerSync's FIRST completed sync. A stashed PATCH-only EDIT to
 * an already-synced row re-applies by colliding with its synced-down capture_items row
 * (requeue-stashed-counts.ts :155), so that row must be present locally first. Right
 * after re-login the local mirror is empty until the captures stream syncs; re-injecting
 * an edit then would emit a create-PUT the backend idempotent-returns WITHOUT applying —
 * silently dropping the field's edit (Codex P1). `hasSynced` flips true once the first
 * full sync completes, after which the edited row is present and the collision → PATCH
 * re-apply fires. A fresh create's row is legitimately absent post-sync, so it still
 * re-INSERTs cleanly. Counts stay safely stashed until then.
 */
function AuthedReinjectStashedWork({
  userId,
  reinject,
}: {
  userId: string
  reinject: Reinjector
}): null {
  const hasSynced = useHasSynced()

  useEffect(() => {
    if (!hasSynced) return

    // Fire on entry — catches a stash left by a prior offline sign-out.
    void runReinject(userId, reinject)

    // Re-run on reconnect — catches a login that happened while still offline.
    // Debounced so a flapping radio can't exhaust the rate limit.
    function onOnline(): void {
      if (Date.now() - lastReinjectAt < MIN_REINJECT_INTERVAL_MS) return
      void runReinject(userId, reinject)
    }
    window.addEventListener('online', onOnline)
    return () => window.removeEventListener('online', onOnline)
  }, [userId, hasSynced, reinject])

  return null
}
